/*
 * Copyright (c) CIRROM 2026.
 */

import React from "react";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { AppliedChange } from "../../api/suggestedchanges";
import { fieldLabel } from "./helpers/normalize";
import { ValueOrEmpty } from "./LedgerRow";
import classes from "./css/recordReview.module.css";

const APPLIED_DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

export const formatAppliedDate = (value: string) => {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? value : APPLIED_DATE_FORMATTER.format(parsed);
};

/*
 * Only WRITTEN has reached the CRM. The other two are decided but still
 * queued, and a steward comparing this list against the CRM needs to know
 * which is which - so the words say where the value is, not how it was
 * approved, and the "who" line carries the approval.
 */
const statusWords = (change: AppliedChange, crmName: string) =>
  change.change_status === "WRITTEN"
    ? `Written to ${crmName}`
    : `Approved, not yet written to ${crmName}`;

const actorWords = (change: AppliedChange) =>
  change.user ? `by ${change.user}` : "automatically";

/*
 * The history half of the ledger: every change already decided for this
 * contact or account, across all batches, newest first. Read-only - these
 * are facts about what happened, and a write to the CRM has no undo here.
 */
export const AppliedChangesList = (props: {
  changes: AppliedChange[];
  crmLabel?: string;
}) => {
  const crmName = props.crmLabel ?? "your CRM";

  if (props.changes.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No changes have been applied to this record yet.
        </Typography>
      </div>
    );
  }

  return (
    <div>
      <div className={classes.appliedColumnHeader}>
        <span />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Was
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Now
        </Typography>
      </div>
      <Divider />
      {props.changes.map((change, index) => {
        const written = change.change_status === "WRITTEN";
        const StatusIcon = written
          ? CheckCircleOutlineOutlinedIcon
          : ScheduleOutlinedIcon;
        return (
          <React.Fragment key={change.suggested_change_id}>
            {index > 0 && <Divider />}
            <div className={classes.appliedRow}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {fieldLabel(change.field)}
              </Typography>
              <Typography
                variant="body2"
                component="div"
                className={classes.appliedValue}
                sx={{ color: "text.secondary" }}
              >
                <ValueOrEmpty value={change.old_value} />
              </Typography>
              <div className={classes.appliedValueColumn}>
                <Typography
                  variant="body2"
                  component="div"
                  className={classes.appliedValue}
                >
                  <ValueOrEmpty value={change.new_value} />
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  className={classes.appliedMeta}
                  sx={{ color: "text.secondary" }}
                >
                  <StatusIcon
                    className={classes.appliedMetaIcon}
                    sx={{ color: written ? "success.main" : "text.secondary" }}
                  />
                  {`${statusWords(change, crmName)} · ${actorWords(
                    change
                  )} · ${formatAppliedDate(change.last_update_time)}`}
                </Typography>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};
